// This is the main App component that sets up the entire application structure
// It imports all the pages (components) and sets up routing

import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'; // Router tools for page navigation
import { Helmet } from 'react-helmet'; // Tool to manage the page title and head tags
import { Agentation } from 'agentation'; // Visual annotation tool for AI-assisted development (dev only)
import MyNavbar from './MyNavbar'; // Navigation bar component shown on every page
import Home from './Home'; // Home/landing page
import Hjem from './Hjem'; // Hjem welcome page
import Regler from './Regler'; // Rules page
import Scoreboard from './Scoreboard'; // Scoreboard/rankings page
import MinePoeng from './Sc_MinePoeng'; // My points page
import { getAuth, onAuthStateChanged, getRedirectResult } from 'firebase/auth'; // Firebase authentication functions
import Login from './Login'; // Login page component
import Login2 from './Login2'; // Alternative login page
import MinProfil from './MinProfil'; // User's personal profile page
import Bilder from './Bilder'; // Pictures/images gallery page
import { app } from './firebase-config'; // Firebase app configuration
import KlRating from './KlRating'; // KL rating (food event rating) page
import Profiler from './Profiler'; // All members/profiles directory page
import PublicProfile from './PublicProfile'; // Individual member's public profile
import Blatur from './Blatur'; // Blåtur trips, participation and rules
import BlaturTur from './BlaturTur'; // Single blåtur trip
import Admin from './Admin'; // Admin for super admins and blåtur organizers
import Invitasjon from './Invitasjon'; // Accept an invitation link


// Set up Firebase authentication
const auth = getAuth(app);

// This is a special component that protects certain pages - only logged-in users can access them
// If a user is not logged in, they get redirected to the login page
const PrivateRoute = ({ children }) => {
  const [user, setUser] = useState(null); // State to store the currently logged-in user
  const [loading, setLoading] = useState(true); // State to show loading while checking authentication

  // This effect runs when the component loads and checks if a user is logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        // User is already logged in
        setUser(currentUser);
      } else {
        // User is not logged in, try to get redirect result from OAuth
        try {
          const result = await getRedirectResult(auth);
          if (result && result.user) {
            setUser(result.user);
          }
        } catch (error) {
          console.error('Error handling redirect result: ', error);
        }
      }
      setLoading(false); // Stop loading
    });
    return () => unsubscribe(); // Clean up when component unmounts
  }, []);

  // While checking authentication, show a loading message
  if (loading) {
    return <div>Loading...</div>;
  }

  // If user is logged in, show the page. If not, redirect to login
  return user ? children : <Navigate to="/login" />;
};

// Main App component that renders the entire application
function App() {
  
  // This effect runs once when the app loads - it sets up analytics tracking
  React.useEffect(() => {
    // Initialize Matomo analytics for tracking user behavior and page visits
   var _mtm = window._mtm = window._mtm || []; // Create or get the analytics tracking queue
   _mtm.push({'mtm.startTime': (new Date().getTime()), 'event': 'mtm.Start'}); // Record the start time
   (function() {
     var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
     g.async=true; g.src='https://cdn.matomo.cloud/kulinarisklaug.matomo.cloud/container_wUtF3AMF.js'; s.parentNode.insertBefore(g,s);
   })();
  }, [])

  return (
    <div className="app-root">
      {/* Set the page title shown in the browser tab */}
      <Helmet>
        <title>LaugHub</title>
      </Helmet>
      
      {/* Router enables navigation between different pages */}
      <Router>
        {/* Show the navigation bar on every page */}
        <MyNavbar />
        
        {/* Container for the main page content */}
        <div className="app-container">
          {/* Define the routes - which component shows for each URL path */}
          <Routes>
            <Route path="/login" element={<Login />} /> {/* Login page */}
            <Route path="/login2" element={<Login2 />} /> {/* Alternative login page */}
            <Route path="/" element={<Hjem />} /> {/* Welcome page */}
            <Route path="/forside" element={<Home />} /> {/* Events overview page */}
            <Route path="/bilder" element={<Bilder />} /> {/* Gallery of pictures from events */}
            <Route path="/scoreboard" element={<Scoreboard />} /> {/* Rankings and points */}
            <Route path="/scoreboard/minepoeng" element={<MinePoeng />} /> {/* My personal points */}
            <Route path="/regler" element={<Regler />} /> {/* Rules of the club */}
            <Route path="/klrating" element={<KlRating />} /> {/* KL food event ratings */}
            {/* This page is protected - only logged in users can access it */}
            <Route path="/minprofil" element={<PrivateRoute><MinProfil /></PrivateRoute>} /> {/* My profile page */}
            <Route path="/profiler" element={<Profiler />} /> {/* Directory of all members */}
            <Route path="/profil/:userId" element={<PublicProfile />} /> {/* Someone else's public profile */}
            <Route path="/blatur" element={<Blatur />} /> {/* Blåtur trips, participation and rules */}
            <Route path="/blatur/:tripId" element={<BlaturTur />} /> {/* Single blåtur trip */}
            <Route path="/admin" element={<Admin />} /> {/* Admin for super admins and blåtur organizers */}
            <Route path="/invitasjon/:token" element={<Invitasjon />} /> {/* Accept an invitation link */}
          </Routes>
        </div>
      </Router>
      {process.env.NODE_ENV === 'development' && <Agentation />}
    </div>
  );
}

export default App;